import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    TimePicker,
    Switch,
    InputNumber,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import type { FormInstance } from 'antd/es/form/Form';
import _ from 'lodash';


const { TextArea } = Input;
const { Option } = Select;
dayjs.extend(duration);


const typeOfWorkOptions = [
    { label: 'Full-time', value: 'full_time' },
    { label: 'Part-time', value: 'part_time' },
    { label: 'Freelance', value: 'freelance' },
    { label: 'Contract', value: 'contract' },
    { label: 'Internship', value: 'internship' },
    { label: 'Temporary', value: 'temporary' },
    { label: 'Remote', value: 'remote' },
    { label: 'On-site', value: 'on_site' },
    { label: 'Hybrid', value: 'hybrid' },
    { label: 'Consulting', value: 'consulting' },
    { label: 'Volunteer', value: 'volunteer' },
    { label: 'Project-based', value: 'project_based' },
    { label: 'Apprenticeship', value: 'apprenticeship' },
    { label: 'Seasonal', value: 'seasonal' },
];

type YesNo = 'Yes' | 'No';


interface PayrollModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        payroll_id?: number;
        payroll_ledger_id?: number;
        payroll_check_in?: string;
        payroll_check_out?: string;
        payroll_working_hours?: string;
        payroll_days_of_working?: string;
        payroll_days_off?: string;
        payroll_date_of_joining?: string;
        payroll_salary?: number;
        payroll_type_of_work?: string;
        payroll_increment_period?: string;
        payroll_increment_type?: string;
        payroll_late_allowance_time?: string;
        payroll_late_apply_half_day: YesNo | null;
        payroll_late_deduct_commission: YesNo | null;
        payroll_late_commission_percentage: number | null;
        payroll_early_go_allowance_time?: string;
        payroll_early_apply_half_day: YesNo | null;
        payroll_early_deduct_commission: YesNo | null;
        payroll_early_commission_percentage: number | null;
    } | null;
    isEditMode?: boolean;
}


interface Ledger {
    ledger_id: number;
    ledger_account_head?: number;
    ah_name?: string;
}


const PayrollModal: React.FC<PayrollModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [ledgers, setLedgers] = useState<Ledger[]>([]);

    const uniqueLedgers = _.uniqBy(ledgers, 'ah_name');
    // Watch both fields
    const incrementType = Form.useWatch('increment_type', form);
    const incrementValue = Form.useWatch('increment_value', form);


    const [showCommissionOption, setShowCommissionOption] = useState(false);
    const [showEarlyGoCommissionOption, setShowEarlyGoCommissionOption] = useState(false);



    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddHc = async (values: any) => {
        try {
            const values = form.getFieldsValue();
            const dataVal = {
                payroll_ledger_id: values.payroll_ledger_id,
                payroll_check_in: values.payroll_check_in
                    ? values.payroll_check_in.format('hh:mm:ss A')  // e.g. "01:30:00 PM"
                    : null,
                payroll_check_out: values.payroll_check_out
                    ? values.payroll_check_out.format('hh:mm:ss A')
                    : null,
                payroll_working_hours: values.payroll_working_hours,
                // payroll_days_of_working: values.payroll_days_of_working,
                payroll_days_of_working: Array.isArray(values.payroll_days_of_working)
                    ? values.payroll_days_of_working.join(', ')
                    : values.payroll_days_of_working,
                payroll_days_off: values.payroll_days_off,
                payroll_date_of_joining: values.payroll_date_of_joining,
                payroll_salary: values.payroll_salary,
                payroll_type_of_work: values.payroll_type_of_work,
                payroll_increment_period: values.payroll_increment_period,
                payroll_increment_type: values.payroll_increment_type,
                payroll_late_allowance_time: values.payroll_late_allowance_time
                    ? values.payroll_late_allowance_time.format('hh:mm:ss A')  // e.g. "01:30:00 PM"
                    : null,
                payroll_late_apply_half_day: values.payroll_late_apply_half_day,
                payroll_late_deduct_commission: values.payroll_late_deduct_commission,
                payroll_late_commission_percentage: values.payroll_late_commission_percentage,

                payroll_early_go_allowance_time: values.payroll_early_go_allowance_time
                    ? values.payroll_early_go_allowance_time.format('hh:mm:ss A')  // e.g. "01:30:00 PM"
                    : null,
                payroll_early_apply_half_day: values.payroll_early_apply_half_day,
                payroll_early_deduct_commission: values.payroll_early_deduct_commission,
                payroll_early_commission_percentage: values.payroll_early_commission_percentage,
                ...(initialValues?.payroll_id && { payroll_id: initialValues.payroll_id }),
            };
            const url = isEditMode
                ? 'edit-payroll'
                : 'add-payroll';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Payroll updated successfully!' : 'Payroll created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} Payroll`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {

            form.setFieldsValue({
                payroll_ledger_id: initialValues.payroll_ledger_id,
                // payroll_check_in: initialValues.payroll_check_in,
                // payroll_check_out: initialValues.payroll_check_out,
                payroll_check_in: initialValues.payroll_check_in ? dayjs(initialValues.payroll_check_in, 'HH:mm A') : null,
                payroll_check_out: initialValues.payroll_check_out ? dayjs(initialValues.payroll_check_out, 'HH:mm A') : null,
                payroll_working_hours: initialValues.payroll_working_hours,
                // payroll_days_of_working: initialValues.payroll_days_of_working,
                payroll_days_of_working: initialValues.payroll_days_of_working
                    ? initialValues.payroll_days_of_working.split(',') // <-- Important
                    : [],
                payroll_days_off: initialValues.payroll_days_off,
                payroll_date_of_joining: initialValues.payroll_date_of_joining ? dayjs(initialValues.payroll_date_of_joining) : null,
                payroll_salary: initialValues.payroll_salary,
                payroll_type_of_work: initialValues.payroll_type_of_work,
                payroll_increment_period: initialValues.payroll_increment_period,
                payroll_increment_type: initialValues.payroll_increment_type,
                payroll_late_allowance_time: initialValues.payroll_late_allowance_time,
                payroll_late_apply_half_day: initialValues.payroll_late_apply_half_day,
                payroll_late_deduct_commission: initialValues.payroll_late_deduct_commission,
                payroll_late_commission_percentage: initialValues.payroll_late_commission_percentage,

                payroll_early_go_allowance_time: initialValues.payroll_early_go_allowance_time,
                payroll_early_apply_half_day: initialValues.payroll_early_apply_half_day,
                payroll_early_deduct_commission: initialValues.payroll_early_deduct_commission,
                payroll_early_commission_percentage: initialValues.payroll_early_commission_percentage,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);

    const handleTimeChange = (
        changedValues: Record<string, any>,
        allValues: Record<string, any>
    ): void => {
        const { payroll_check_in, payroll_check_out } = allValues;

        if (payroll_check_in && payroll_check_out) {
            const start = dayjs(payroll_check_in);
            const end = dayjs(payroll_check_out);

            let diff = end.diff(start, 'minute');
            if (diff < 0) {
                diff += 24 * 60; // handle overnight shifts
            }

            const hours = Math.floor(diff / 60);
            const minutes = diff % 60;

            const result = minutes > 0 ? `${hours}h ${minutes}m` : `${hours} hours`;

            form.setFieldsValue({ payroll_working_hours: result });
        }
    };




    const fetchLedgers = async () => {
        try {
            const response = await apiRequest({
                method: 'get',
                url: 'ledger-data',
            });

            // Fix: Set only the array, not the full object
            const ledgerArray = response.ledgers;

            if (Array.isArray(ledgerArray)) {
                setLedgers(ledgerArray);
            } else {
                setLedgers([]); // Fallback if unexpected
            }
        } catch (error) {
            showError('Failed to fetch ledger data');
            console.error(error);
        }
    };


    useEffect(() => {
        fetchLedgers();
    }, []);


    // When either field changes, update the final field
    useEffect(() => {
        if (incrementType && incrementValue !== undefined) {
            const finalValue =
                incrementType === 'percentage'
                    ? `${incrementValue}%`
                    : `₹${incrementValue}`;
            form.setFieldsValue({ payroll_increment_type: finalValue });
        }
    }, [incrementType, incrementValue]);





    return (
        <Modal
            title={isEditMode ? 'Edit Payroll' : 'Add New Payroll'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
            centered
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddHc}
                autoComplete="off"
                onValuesChange={handleTimeChange}
            >
                <Row gutter={16}>
                    {/* <Col span={12}>
                        <Form.Item
                            name="payroll_ledger_id"
                            className="px-8 "
                            label="Account Name"
                            rules={[{ required: true, message: 'Please enter Check-In time' }]}
                        >
                            <Select
                                placeholder="Select Ledger"
                                style={{ width: '100%' }}
                                allowClear
                            >
                                {Array.isArray(ledgers) &&
                                    ledgers.map((l: Ledger) => (
                                        <Option key={l.ledger_id} value={l.ledger_id}>
                                            {l.ah_name || `Ledger #${l.ledger_id}`}
                                        </Option>
                                    ))}
                            </Select>
                        </Form.Item>
                    </Col> */}

                    {/* Account Name */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_ledger_id"
                            className="px-8"
                            label="Account Name"
                            rules={[{ required: true, message: 'Please enter Check-In time' }]}
                        >
                            <Select
                                placeholder="Select Ledger"
                                style={{ width: '100%' }}
                                allowClear
                                showSearch
                                optionFilterProp="children"
                            >
                                {Array.isArray(uniqueLedgers) &&
                                    uniqueLedgers.map((l: Ledger) => (
                                        <Option key={l.ledger_id} value={l.ledger_id}>
                                            {l.ah_name || `Ledger #${l.ledger_id}`}
                                        </Option>
                                    ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Check-In Time */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_check_in"
                            className="px-8 "
                            label="Check-In Time"
                            rules={[{ required: true, message: 'Please enter Check-In time' }]}
                        >
                            <TimePicker format="hh:mm A" style={{ width: "100%" }}
                                className="simple-input-radius-with-border" use12Hours />
                        </Form.Item>
                    </Col>

                    {/*Check-Out Time  */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_check_out"
                            className="px-8"
                            label="Check-Out Time"
                            rules={[{ required: true, message: 'Please enter Check-Out time' }]}
                        >
                            <TimePicker format="hh:mm A" style={{ width: "100%" }}
                                className="simple-input-radius-with-border" use12Hours />
                        </Form.Item>
                    </Col>

                    {/* Working Hours */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_working_hours"
                            className="px-8"
                            label="Working Hours"
                            rules={[{ required: true, message: 'Please enter Working Hours' }]}
                        >
                            <Input placeholder="e.g., 8 hours" disabled />
                        </Form.Item>
                    </Col>

                    {/* Days of Working */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_days_of_working"
                            className="px-8"
                            label="Days of Working"
                            rules={[
                                { required: true, message: 'Please select Days of Working' },
                            ]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="e.g., Monday to Friday"
                                showSearch
                                optionFilterProp="children"
                                allowClear
                                className='input-bordor-radius-with-border'
                            >
                                <Select.Option value="Monday">Monday</Select.Option>
                                <Select.Option value="Tuesday">Tuesday</Select.Option>
                                <Select.Option value="Wednesday">Wednesday</Select.Option>
                                <Select.Option value="Thursday">Thursday</Select.Option>
                                <Select.Option value="Friday">Friday</Select.Option>
                                <Select.Option value="Saturday">Saturday</Select.Option>
                                <Select.Option value="Sunday">Sunday</Select.Option>
                            </Select>
                        </Form.Item>

                    </Col>

                    {/* Days Off */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_days_off"
                            className="px-8"
                            label="Days Off"
                            rules={[{ required: true, message: 'Please select Days Off' }]}
                        >
                            <Select placeholder="Select a day" allowClear showSearch
                                optionFilterProp="children">
                                <Select.Option value="Monday">Monday</Select.Option>
                                <Select.Option value="Tuesday">Tuesday</Select.Option>
                                <Select.Option value="Wednesday">Wednesday</Select.Option>
                                <Select.Option value="Thursday">Thursday</Select.Option>
                                <Select.Option value="Friday">Friday</Select.Option>
                                <Select.Option value="Saturday">Saturday</Select.Option>
                                <Select.Option value="Sunday">Sunday</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Date of Joining */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_date_of_joining"
                            className="px-8"
                            label="Date of Joining"
                            rules={[{ required: true, message: 'Please select Date of Joining' }]}
                        >
                            <DatePicker style={{ width: '100%' }} className="simple-input-radius-with-border" format="DD-MM-YYYY" />
                        </Form.Item>
                    </Col>

                    {/* Salary */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_salary"
                            className="px-8"
                            label="Salary"
                            rules={[{ required: true, message: 'Please enter Salary' }]}
                        >
                            <Input type="number" placeholder="e.g., 30000" />
                        </Form.Item>
                    </Col>

                    {/* Type of Work */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_type_of_work"
                            className="px-9"
                            label="Type of Work"
                            rules={[
                                { required: true, message: 'Please enter Type of Work' },
                                { max: 50, message: 'Maximum 30 characters allowed' },
                            ]}
                        >
                            {/* <Input showCount maxLength={30} placeholder="e.g., Full-time" /> */}
                            <Select
                                showSearch
                                placeholder="Select type of work"
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={typeOfWorkOptions}
                            />
                        </Form.Item>
                    </Col>

                    {/* Increment Period */}
                    <Col span={12}>
                        <Form.Item
                            name="payroll_increment_period"
                            className="px-8"
                            label="Increment Period"
                            rules={[
                                { required: true, message: 'Please select Increment Period' },
                            ]}
                        >
                            <Select
                                placeholder="Select increment period"
                                showSearch
                                optionFilterProp="children"
                                allowClear
                            >
                                <Select.Option value="Monthly">Monthly</Select.Option>
                                <Select.Option value="Quarterly">Quarterly</Select.Option>
                                <Select.Option value="Annually">Annually</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* <Col span={12}>
                        <Form.Item
                            name="payroll_increment_type"
                            className="px-8"
                            label="Increment Type"
                            rules={[
                                { required: true, message: 'Please enter Increment Type' },
                                { max: 30, message: 'Maximum 30 characters allowed' },
                            ]}
                        >
                            <Input showCount maxLength={30} placeholder="e.g., Percentage" />
                        </Form.Item>
                    </Col> */}
                </Row>

                {/* .Increment Type */}
                <Row gutter={16}>
                    <Col span={4} style={{ paddingRight: 0 }}>
                        <Form.Item
                            name="increment_type"
                            label="Type"
                            rules={[{ required: true, message: 'Please select type' }]}
                        >
                            <Select placeholder="Select type" className='left-rounded'>
                                <Option value="percentage">Percentage (%)</Option>
                                <Option value="rupees">Rupees (₹)</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8} style={{ paddingLeft: 0 }}>
                        <Form.Item
                            name="increment_value"
                            label="Value"
                            rules={[{ required: true, message: 'Please enter value' }]}
                        >
                            <Input type="number" placeholder="Enter value" className='right-rounded' />
                        </Form.Item>
                    </Col>
                </Row>


                {/* Late come */}
                <Row gutter={16}>
                    {/* Late Arrival Allowance (Times) */}
                    <Col span={6} style={{ paddingLeft: 0 }}>
                        <Form.Item
                            name="payroll_late_allowance_time"
                            className="px-8 "
                            label="Late Arrival Allowance (Times)"
                            rules={[{ required: true, message: 'Please enter late allowance' }]}
                        >
                            <TimePicker format="hh:mm A" style={{ width: "100%" }}
                                className="simple-input-radius-with-border" use12Hours />
                        </Form.Item>

                    </Col>

                    {/* Apply Half Day if Late exceeds Allowance? */}
                    <Col span={6} style={{ paddingLeft: 0 }}>
                        <Form.Item
                            name="payroll_late_apply_half_day"
                            className="px-8"
                            label="Apply Half Day if Late exceeds Allowance?"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>

                    {/* Deduct Commission if Late exceeds Allowance? */}
                    <Col span={6} style={{ paddingLeft: 0 }}>
                        <Form.Item
                            name="payroll_late_deduct_commission"
                            className="px-8"
                            label="Deduct Commission if Late exceeds Allowance?"
                            valuePropName="checked"
                        >
                            <Switch onChange={setShowCommissionOption} />
                        </Form.Item>
                    </Col>

                    {/* Commission Deduction (%) */}
                    <Col span={6} style={{ paddingLeft: 0 }}>
                        <Form.Item
                            name="payroll_late_commission_percentage"
                            className="px-8"
                            label="Commission Deduction (%)"
                            rules={[
                                {
                                    required: showCommissionOption,
                                    message: 'Please select a percentage',
                                },
                            ]}
                        >
                            <Select
                                placeholder="Select commission percentage"
                                disabled={!showCommissionOption}
                            >
                                {[5, 10, 15, 20, 25, 50, 100].map((percent) => (
                                    <Option key={percent} value={percent}>
                                        {percent}%
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>


                    {/* Early go */}
                    {/* Early Go Allowance (Time) */}
                    <Col span={6} style={{ paddingLeft: 0 }}>
                        <Form.Item
                            name="payroll_early_go_allowance_time"
                            className="px-8"
                            label="Early Go Allowance (Time)"
                            rules={[{ required: true, message: 'Please enter early go allowance time' }]}
                        >
                            <TimePicker
                                format="hh:mm A"
                                style={{ width: "100%" }}
                                className="simple-input-radius-with-border"
                                use12Hours
                            />
                        </Form.Item>
                    </Col>

                    {/* Apply Half Day if Early Go exceeds Allowance? */}
                    <Col span={6} style={{ paddingLeft: 0 }}>
                        <Form.Item
                            name="payroll_early_apply_half_day"
                            className="px-8"
                            label="Apply Half Day if Early Go exceeds Allowance?"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>

                    {/* Deduct Commission if Early Go exceeds Allowance? */}
                    <Col span={6} style={{ paddingLeft: 0 }}>
                        <Form.Item
                            name="payroll_early_deduct_commission"
                            className="px-8"
                            label="Deduct Commission if Early Go exceeds Allowance?"
                            valuePropName="checked"
                        >
                            <Switch onChange={setShowEarlyGoCommissionOption} />
                        </Form.Item>
                    </Col>

                    {/* Commission Deduction for Early Go (%) */}
                    <Col span={6} style={{ paddingLeft: 0 }}>
                        <Form.Item
                            name="payroll_early_commission_percentage"
                            className="px-8"
                            label="Commission Deduction for Early Go (%)"
                            rules={[
                                {
                                    required: showEarlyGoCommissionOption,
                                    message: 'Please select a percentage',
                                },
                            ]}
                        >
                            <Select
                                placeholder="Select commission percentage"
                                disabled={!showEarlyGoCommissionOption}
                            >
                                {[5, 10, 15, 20, 25, 50, 100].map((percent) => (
                                    <Option key={percent} value={percent}>
                                        {percent}%
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                </Row>

                {/* Hidden field that combines both */}
                <Form.Item name="payroll_increment_type" noStyle>
                    <Input type="hidden" />
                </Form.Item>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Payroll
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PayrollModal;
